import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "tailwindcss/tailwind.css";
import key from "../../Assets/ContentCreation.jpg";
import key1 from "../../Assets/GraphicDesign.jpg";
import key2 from "../../Assets/SocialMedia.jpg";
import key3 from "../../Assets/About/About/use.jpg";
import key4 from "../../Assets/me-home.jpg";
import key5 from "../../Assets/web-design.jpg";
import key6 from "../../Assets/Hero-image.jpg";
import key7 from "../../Assets/Engagement-Management.jpg";
import key8 from "../../Assets/branding.jpg";
import key9 from "../../Assets/use-10.jpg";

gsap.registerPlugin(ScrollTrigger);

const contentData = [
  {
    heading: "Content Creation",
    bullets: [
      "Professional photography and videography (1-hour sessions or custom durations).",
      "Short-form video content (Instagram Reels, TikTok).",
      "Behind-the-scenes or team highlight shoots at your location.",
    ],
    image: key,
  },
  {
    heading: "Graphic Design & Branding",
    bullets: [
      "Custom business cards, letterheads, and envelopes.",
      "Logo design and complete rebranding.",
      "Brand style guides (color palettes, fonts, etc.).",
    ],
    image: key1,
  },
  {
    heading: "Social Media Enhancements",
    bullets: [
      "Additional posts and reels (beyond your package).",
      "Custom-designed templates for posts, stories, or ads.",
      "Interactive story content (polls, quizzes, etc.).",
    ],
    image: key2,
  },
  {
    heading: "Marketing Collateral",
    bullets: [
      "Flyers, brochures, and pamphlets.",
      "Gift certificates or voucher templates.",
      "Custom email signatures.",
    ],
    image: key3,
  },
  {
    heading: "Print Services",
    bullets: [
      "Printing for business cards, brochures, and posters.",
      "High-quality event banners and wall decor.",
    ],
    image: key4,
  },
  {
    heading: "Video Editing",
    bullets: [
      "Editing for YouTube videos, presentations, and animations.",
      "Captions, overlays, and dynamic graphics for videos.",
    ],
    image: key5,
  },
  {
    heading: "Strategic Growth Support",
    bullets: [
      "Competitor analysis and social media audits.",
      "Personalized marketing strategy consultations.",
    ],
    image: key6,
  },
  {
    heading: "Reputation & Engagement Management",
    bullets: [
      "Managing and replying to Google and social media reviews.",
      "Comment monitoring and audience engagement.",
    ],
    image: key7,
  },
  {
    heading: "In-Office Marketing & Branding",
    bullets: [
      "Wall art, branded decor, and event promotion materials.",
      "Monthly creation of up to 3 flyers or brochures.",
    ],
    image: key8,
  },
  {
    heading: "Custom Add-Ons",
    bullets: [
      "Seasonal content and holiday campaigns.",
      "Social media giveaways and contest management.",
      "Onsite content capture for special events.",
    ],
    image: key9,
  },
];

const Section4 = () => {
  const sectionRef = useRef(null);
  const contentRefs = useRef([]);
  const imageRefs = useRef([]);

  useEffect(() => {
    // Ensure refs array length matches contentData length
    contentRefs.current = contentRefs.current.slice(0, contentData.length);
    imageRefs.current = imageRefs.current.slice(0, contentData.length);

    // Set initial state for content
    gsap.set(contentRefs.current, { y: "180vh", opacity: 0 });
    gsap.set(contentRefs.current[0], { y: 0, opacity: 1 });
    gsap.set(imageRefs.current, { opacity: 0, display: "none" });
    gsap.set(imageRefs.current[0], { opacity: 1, display: "block" });

    const sections = contentData.length;
    const sectionHeight = window.innerHeight;
    let lastIndex = 0;

    // ScrollTrigger logic for content animation
    ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top top",
      end: `+=${(sections - 1) * sectionHeight}px`,
      pin: true,
      scrub: 1,
      snap: {
        snapTo: 1 / (sections - 1),
        duration: 0.5,
        inertia: false,
        ease: "power1.inOut",
      },
      onUpdate: (self) => {
        const progress = self.progress;
        const currentIndex = Math.round(progress * (sections - 1));

        if (currentIndex !== lastIndex) {
          contentData.forEach((_, index) => {
            if (index === currentIndex) {
              // Move current content to the center
              gsap.to(contentRefs.current[index], {
                y: 0,
                opacity: 1,
                duration: 0.9,
                ease: "power2.out",
              });
              gsap.to(imageRefs.current[index], {
                opacity: 1,
                display: "block",
                duration: 0.5,
                ease: "power2.out",
              });
            } else {
              // Move content up or reset to the bottom
              gsap.to(contentRefs.current[index], {
                y: index < currentIndex ? "-150vh" : "180vh",
                opacity: 0,
                duration: 0.5,
                ease: "power2.in",
              });
              gsap.to(imageRefs.current[index], {
                opacity: 0,
                display: "none",
                duration: 0.5,
                ease: "power2.in",
              });
            }
          });

          lastIndex = currentIndex;
        }
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div
      ref={sectionRef}
      className="w-full h-screen overflow-hidden relative bg-white "
    >
      <h1
        className="text-4xl font-bold text-center lg:mt-24"
        style={{
          fontFamily: "Blacker Pro Display",
        }}
      >
        Add-On Services
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 w-full h-screen ">
        {/* Content */}
        <div
          className="relative h-full flex items-center justify-center order-2 lg:order-1 "
          style={{ fontFamily: "Blacker Pro Display" }}
        >
          {contentData.map((content, index) => (
            <div
              key={index}
              ref={(el) => (contentRefs.current[index] = el)}
              className="absolute w-full px-8 text-center"
            >
              <h2 className="text-2xl lg:text-4xl font-extrabold mb-6 text-gray-900 ">
                {content.heading}
              </h2>
              <ul className="list-disc inline-block text-left text-lg text-gray-700">
                {content.bullets.map((bullet, i) => (
                  <li key={i} className="mb-2">
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Image */}
        <div className="relative h-full flex items-center justify-center order-1 lg:order-2">
          {contentData.map((content, index) => (
            <div
              key={index}
              ref={(el) => (imageRefs.current[index] = el)}
              className="absolute inset-0 flex items-center justify-center opacity-0 lg:w-[600px] lg:h-[600px] xs:p:2 xs:w-[200px] xs:h-[200px] lg:mt-8 mx-auto"
            >
              <img
                src={content.image}
                alt={`Image ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Section4;
